import { deserify } from '@geneo2-web/shared-ui';
import { useMemo } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from './store';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Selects a value from the Redux store and `deserify`s it (converts serified
 * `"BigInt(n)"` strings back to real bigint, etc.).
 *
 * IMPORTANT: select the SPECIFIC field you need, not a whole slice. The result
 * is memoized on the raw selected reference (which Immer keeps stable across
 * unrelated writes to the same slice), so:
 *   - you can never forget `deserify` on a bigint field, and
 *   - the returned value is a stable reference safe to put in effect deps.
 *
 * @example
 * const userInfo = useDeserifiedSelector((s) => s.auth.userInfo);
 */
export function useDeserifiedSelector<T>(
  selector: (state: RootState) => T
): T {
  const raw = useAppSelector(selector);
  return useMemo(() => deserify(raw), [raw]);
}
